import Konva from 'konva';
import { jsPDF } from 'jspdf';
import type { Guest, ContentBounds, TableConfig } from './types.ts';
import {
  $placedGuests,
  $occupiedSeats,
  $customFields,
  $currentVersionId,
  $availableVersions
} from './state.ts';
import {
  SEAT_RADIUS,
  SEAT_TEXT_OFFSET,
  RECTANGLE_WIDTH,
  RECTANGLE_HEIGHT_6,
  RECTANGLE_HEIGHT_7,
  NAME_HOVER_BG_COLOR,
  LINE_HEIGHT,
  FONT_SIZE,
  CONTENT_PADDING,
  SEAT_OCCUPIED_COLOR,
  SEAT_EMPTY_COLOR
} from './constants.ts';
import { getLayout } from './domain/layout.ts';

let stage: Konva.Stage;
let layer: Konva.Layer;
let dragLayer: Konva.Layer;

// --- Helper Functions for DRY Code ---

/**
 * Creates a standardized Konva Text with default Lato font
 */
function createText(config: Konva.TextConfig): Konva.Text {
  return new Konva.Text({
    fontFamily: 'Lato, Arial, sans-serif',
    ...config,
  });
}

/**
 * Creates a standardized hover effect for interactive elements
 */
function addHoverEffect(
  group: Konva.Group, 
  backgroundShape: Konva.Shape, 
  hoverColor: string, 
  defaultColor: string
): void {
  group.on('mouseenter', () => {
    stage.container().style.cursor = 'grab';
    if (!group.isDragging()) {
      backgroundShape.fill(hoverColor);
      layer.batchDraw();
    }
  });
  
  group.on('mouseleave', () => {
    stage.container().style.cursor = 'default';
    backgroundShape.fill(defaultColor);
    layer.batchDraw();
  });
}

/**
 * Calculates text rotation and determines if text should be flipped
 */
function calculateTextRotation(angle: number): { rotation: number; isFlipped: boolean } {
  const angleDegrees = angle * 180 / Math.PI;
  let textRotation = angleDegrees + 90;
  
  // Normalize rotation to 0-360 range
  while (textRotation < 0) textRotation += 360;
  while (textRotation >= 360) textRotation -= 360;
  
  // Flip text if it would be upside down
  const isFlipped = textRotation > 90 && textRotation < 270;
  if (isFlipped) {
    textRotation += 180;
  }
  
  return { rotation: textRotation, isFlipped };
}

/**
 * Creates a background rectangle with padding for hover effects
 */
function createBackgroundRect(group: Konva.Group, padding: number = 4): Konva.Rect {
  const textBounds = group.getClientRect({ relativeTo: group });
  const bg = new Konva.Rect({
    x: textBounds.x - padding,
    y: textBounds.y - padding,
    width: textBounds.width + padding * 2,
    height: textBounds.height + padding * 2,
    fill: 'transparent',
    cornerRadius: 3,
    name: 'bg'
  });
  group.add(bg);
  bg.moveToBottom();
  return bg;
}

export function initializeKonva(containerWidth: number, containerHeight: number): Promise<void> {
  return new Promise((resolve) => {
    stage = new Konva.Stage({
      container: 'container',
      width: containerWidth,
      height: containerHeight,
      draggable: true,
    });

    layer = new Konva.Layer();
    stage.add(layer);

    dragLayer = new Konva.Layer();
    stage.add(dragLayer);

    // Ensure Lato font is loaded before resolving
    document.fonts.ready.then(() => {
      // Check if Lato font is loaded
      if (document.fonts.check('1em Lato')) {
        resolve();
      } else {
        // Fallback - try with a small delay (common issue with some browsers)
        setTimeout(() => {
          resolve();
        }, 500);
      }
    }).catch(() => {
      // Even if font loading fails, continue with fallback fonts
      resolve();
    });
  });
}

export function getStage(): Konva.Stage {
  return stage;
}

export function getLayer(): Konva.Layer {
  return layer;
}

export function getDragLayer(): Konva.Layer {
  return dragLayer;
}


/**
 * Calculate seat positions for rectangle tables
 * Rectangle 6-seat: 3 on left, 3 on right (clockwise from bottom-left: BL, ML, TL, TR, MR, BR)
 * Rectangle 7-seat: Same as 6-seat + 1 at top center
 */
interface SeatPosition {
  x: number;
  y: number;
  angle: number; // Angle for text rotation (0 = right, Math.PI/2 = down, Math.PI = left, -Math.PI/2 = up)
}

function getRectangleSeatPositions(tableConfig: TableConfig): SeatPosition[] {
  const width = tableConfig.width || RECTANGLE_WIDTH;
  const height = tableConfig.height || (tableConfig.type === 'rectangle_7' ? RECTANGLE_HEIGHT_7 : RECTANGLE_HEIGHT_6);
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  
  const positions: SeatPosition[] = [];
  
  if (tableConfig.type === 'rectangle_6') {
    // 6 seats: 3 on left, 3 on right
    // Clockwise numbering from bottom-left: 1(BL), 2(ML), 3(TL), 4(TR), 5(MR), 6(BR)
    const leftX = -halfWidth;
    const rightX = halfWidth;
    const seatSpacing = height / 4; // Divide into 4 sections for 3 seats
    
    // Left side (bottom to top): seats 1, 2, 3
    positions.push(
      { x: leftX, y: halfHeight - seatSpacing, angle: Math.PI }, // Seat 1 (bottom-left)
      { x: leftX, y: 0, angle: Math.PI }, // Seat 2 (middle-left)
      { x: leftX, y: -halfHeight + seatSpacing, angle: Math.PI } // Seat 3 (top-left)
    );
    
    // Right side (top to bottom): seats 4, 5, 6
    positions.push(
      { x: rightX, y: -halfHeight + seatSpacing, angle: 0 }, // Seat 4 (top-right)
      { x: rightX, y: 0, angle: 0 }, // Seat 5 (middle-right)
      { x: rightX, y: halfHeight - seatSpacing, angle: 0 } // Seat 6 (bottom-right)
    );
  } else if (tableConfig.type === 'rectangle_7') {
    // 7 seats: 3 on left, 1 on top, 3 on right
    // Clockwise from bottom-left: 1(BL), 2(ML), 3(TL), 4(Top), 5(TR), 6(MR), 7(BR)
    const leftX = -halfWidth;
    const rightX = halfWidth;
    const seatSpacing = height / 4;
    
    // Left side (bottom to top): seats 1, 2, 3
    positions.push(
      { x: leftX, y: halfHeight - seatSpacing, angle: Math.PI }, // Seat 1 (bottom-left)
      { x: leftX, y: 0, angle: Math.PI }, // Seat 2 (middle-left)
      { x: leftX, y: -halfHeight + seatSpacing, angle: Math.PI } // Seat 3 (top-left)
    );
    
    // Top center: seat 4
    positions.push(
      { x: 0, y: -halfHeight, angle: -Math.PI / 2 } // Seat 4 (top-center)
    );
    
    // Right side (top to bottom): seats 5, 6, 7
    positions.push(
      { x: rightX, y: -halfHeight + seatSpacing, angle: 0 }, // Seat 5 (top-right)
      { x: rightX, y: 0, angle: 0 }, // Seat 6 (middle-right)
      { x: rightX, y: halfHeight - seatSpacing, angle: 0 } // Seat 7 (bottom-right)
    );
  }
  
  return positions;
}

function drawRectangleTable(tableConfig: TableConfig, tableGroup: Konva.Group): void {
  const placedGuests = $placedGuests.get();
  const occupiedSeats = $occupiedSeats.get();
  const width = tableConfig.width || RECTANGLE_WIDTH;
  const height = tableConfig.height || (tableConfig.type === 'rectangle_7' ? RECTANGLE_HEIGHT_7 : RECTANGLE_HEIGHT_6);
  
  // Draw rectangle table
  const tableRect = new Konva.Rect({
    x: -width / 2,
    y: -height / 2,
    width: width,
    height: height,
    fill: '#FFFFFF',
    stroke: '#BDBDBD',
    strokeWidth: 2,
    cornerRadius: 4
  });
  tableGroup.add(tableRect);
  
  // Draw table number
  const tableText = createText({
    text: `${tableConfig.tableNumber}`,
    fontSize: 24,
    fill: '#333'
  });
  tableText.x(-tableText.width() / 2);
  tableText.y(-tableText.height() / 2);
  tableGroup.add(tableText);
  
  // Draw seats
  const seatPositions = getRectangleSeatPositions(tableConfig);
  seatPositions.forEach((pos, index) => {
    const seatNum = index + 1;
    const seatKey = `${tableConfig.tableNumber}-${seatNum}`;
    const isOccupied = occupiedSeats.has(seatKey);
    const guest = placedGuests.find(g => g.table === tableConfig.tableNumber && g.seat === seatNum);
    
    // Draw seat circle
    const seat = new Konva.Circle({
      x: pos.x,
      y: pos.y,
      radius: SEAT_RADIUS,
      fill: isOccupied ? SEAT_OCCUPIED_COLOR : SEAT_EMPTY_COLOR,
      stroke: '#9E9E9E',
      strokeWidth: 1,
      tableNumber: tableConfig.tableNumber,
      seatNumber: seatNum,
      name: 'seat'
    });
    tableGroup.add(seat);
    
    // Add seat number text
    const seatNumberText = createText({
      text: seatNum.toString(),
      x: pos.x,
      y: pos.y,
      fontSize: 14,
      fill: isOccupied ? '#FFFFFF' : '#555555',
      fontStyle: 'bold'
    });
    seatNumberText.x(pos.x - seatNumberText.width() / 2);
    seatNumberText.y(pos.y - seatNumberText.height() / 2);
    tableGroup.add(seatNumberText);
    
    // Draw guest name and custom fields if occupied
    if (guest) {
      drawGuestName(guest, tableGroup, pos.x, pos.y, pos.angle);
      drawCustomFields(guest, tableGroup, pos.x, pos.y, pos.angle);
    }
  });
}

export function drawTables(): void {
  const layout = getLayout();
  
  // Iterate through all table configurations
  layout.tables.forEach(tableConfig => {
    const tableGroup = new Konva.Group({ 
      x: tableConfig.x, 
      y: tableConfig.y, 
      draggable: false 
    });
    layer.add(tableGroup);
    
    // Draw based on table type
    if (tableConfig.type === 'rectangle_6' || tableConfig.type === 'rectangle_7') {
      drawRectangleTable(tableConfig, tableGroup);
    }
    // Future: add support for round tables if needed
  });
}

function drawGuestName(guest: Guest, tableGroup: Konva.Group, seatX: number, seatY: number, angle: number): void {
  const nameParts = guest.fullName.split(' ');
  const { rotation, isFlipped } = calculateTextRotation(angle);
  
  // Calculate text distance - account for multiple lines when flipped
  let textDistance = SEAT_RADIUS + SEAT_TEXT_OFFSET;
  if (isFlipped && nameParts.length > 1) {
    textDistance += (nameParts.length - 1) * LINE_HEIGHT;
  }
  
  // Position text at the tangent of the seat circle
  const nameX = seatX + textDistance * Math.cos(angle);
  const nameY = seatY + textDistance * Math.sin(angle);
  
  // Create a group for the rotated text
  const nameGroup = new Konva.Group({
    x: nameX,
    y: nameY,
    rotation,
    name: 'guestName',
    visible: true,
    draggable: true,
    guestId: guest.id
  });
  
  // Store original position for snap-back on invalid drop
  nameGroup.setAttr('originalX', nameX);
  nameGroup.setAttr('originalY', nameY);
  
  // Create multiple text lines for each word
  nameParts.forEach((word, index) => {
    const wordText = createText({ 
      text: word, 
      fontSize: FONT_SIZE, 
      fill: '#2c5530',
      fontStyle: 'bold',
      align: 'center'
    });
    
    const yOffset = -((nameParts.length - 1 - index) * LINE_HEIGHT);
    wordText.x(-wordText.width() / 2);
    wordText.y(yOffset - wordText.height() / 2);
    nameGroup.add(wordText);
  });
  
  // Add background rectangle and hover effects using helper functions
  const nameBg = createBackgroundRect(nameGroup);
  nameBg.name('nameBg');
  addHoverEffect(nameGroup, nameBg, NAME_HOVER_BG_COLOR, 'transparent');

  tableGroup.add(nameGroup);
}

export function createGuestNameGroup(guest: Guest): Konva.Group {
  const nameParts = guest.fullName.split(' ');

  const nameGroup = new Konva.Group({
    x: 0,
    y: 0,
    rotation: 0,
    name: 'guestName',
    visible: true,
    draggable: true,
    guestId: guest.id
  });

  nameGroup.setAttr('originalX', 0);
  nameGroup.setAttr('originalY', 0);

  nameParts.forEach((word, index) => {
    const wordText = createText({
      text: word,
      fontSize: FONT_SIZE,
      fill: '#2c5530',
      fontStyle: 'bold',
      align: 'center'
    });

    const yOffset = -((nameParts.length - 1 - index) * LINE_HEIGHT);
    wordText.x(-wordText.width() / 2);
    wordText.y(yOffset - wordText.height() / 2);
    nameGroup.add(wordText);
  });

  const nameBg = createBackgroundRect(nameGroup);
  nameBg.name('nameBg');
  addHoverEffect(nameGroup, nameBg, NAME_HOVER_BG_COLOR, 'transparent');

  return nameGroup;
}

function drawBar(): void {
  const layout = getLayout();
  
  // Position bar on the FAR left side with significant spacing from tables
  const barWidth = 80;
  const barHeight = 200;
  const barX = -100;  // Far left of the venue (much further from tables)
  
  // Vertically center the bar in the middle of all tables
  const allTables = layout.tables;
  const minY = Math.min(...allTables.map(t => t.y));
  const maxY = Math.max(...allTables.map(t => t.y));
  const barY = (minY + maxY) / 2;  // Vertical center of table layout
  
  const barGroup = new Konva.Group({ x: barX, y: barY });
  layer.add(barGroup);
  
  // Bar rectangle
  const barRect = new Konva.Rect({
    x: -barWidth / 2,
    y: -barHeight / 2,
    width: barWidth,
    height: barHeight,
    fill: '#E8E8E8',
    stroke: '#999999',
    strokeWidth: 2,
    cornerRadius: 4
  });
  barGroup.add(barRect);
  
  // Bar text
  const barText = createText({
    text: 'Bar',
    fontSize: 18,
    fill: '#333',
    fontStyle: 'bold'
  });
  barText.x(-barText.width() / 2);
  barText.y(-barText.height() / 2);
  barGroup.add(barText);
}

function drawStage(): void {
  const layout = getLayout();
  
  // Position stage at the bottom center
  const stageWidth = 300;
  const stageHeight = 200;
  
  // Calculate center X based on all tables
  const allTables = layout.tables;
  const minX = Math.min(...allTables.map(t => t.x));
  const maxX = Math.max(...allTables.map(t => t.x));
  const centerX = (minX + maxX) / 2;
  
  // Position below all tables
  const maxY = Math.max(...allTables.map(t => t.y));
  const stageY = maxY + 300;  // Below the bottom tables
  
  const stageGroup = new Konva.Group({ x: centerX, y: stageY });
  layer.add(stageGroup);
  
  // Stage rectangle
  const stageRect = new Konva.Rect({
    x: -stageWidth / 2,
    y: -stageHeight / 2,
    width: stageWidth,
    height: stageHeight,
    fill: '#F5F5F5',
    stroke: '#999999',
    strokeWidth: 2,
    cornerRadius: 4
  });
  stageGroup.add(stageRect);
  
  // Stage icon (simplified curtain)
  const curtainGroup = new Konva.Group({
    x: 0,
    y: -10
  });
  stageGroup.add(curtainGroup);
  
  // Simple curtain representation
  const curtainRect = new Konva.Rect({
    x: -20,
    y: -15,
    width: 40,
    height: 20,
    fill: '#CCCCCC',
    stroke: '#999999',
    strokeWidth: 1,
    cornerRadius: 2
  });
  curtainGroup.add(curtainRect);
  
  // Stage label
  const stageText = createText({
    text: "8x12' Stage",
    fontSize: 16,
    fill: '#333',
    fontStyle: 'bold'
  });
  stageText.x(-stageText.width() / 2);
  stageText.y(15);
  stageGroup.add(stageText);
}

export function drawChart(): void {
  // Draw decorative elements
  drawBar();
  drawStage();
  
  // Draw tables
  drawTables();
}

export function calculateContentBounds(): ContentBounds {
  const layout = getLayout();
  
  if (layout.tables.length === 0) {
    return {
      left: 0,
      top: 0,
      right: 1000,
      bottom: 1000,
      width: 1000,
      height: 1000
    };
  }
  
  // Calculate bounds from all table positions
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  
  layout.tables.forEach(table => {
    const width = table.width || RECTANGLE_WIDTH;
    const height = table.height || (table.type === 'rectangle_7' ? RECTANGLE_HEIGHT_7 : RECTANGLE_HEIGHT_6);
    
    const tableLeft = table.x - width / 2;
    const tableRight = table.x + width / 2;
    const tableTop = table.y - height / 2;
    const tableBottom = table.y + height / 2;
    
    minX = Math.min(minX, tableLeft);
    maxX = Math.max(maxX, tableRight);
    minY = Math.min(minY, tableTop);
    maxY = Math.max(maxY, tableBottom);
  });
  
  return {
    left: minX - CONTENT_PADDING,
    right: maxX + CONTENT_PADDING,
    top: minY - CONTENT_PADDING,
    bottom: maxY + CONTENT_PADDING,
    width: (maxX + CONTENT_PADDING) - (minX - CONTENT_PADDING),
    height: (maxY + CONTENT_PADDING) - (minY - CONTENT_PADDING)
  };
}


export function findDropTarget(): Konva.Node | null {
  const pos = stage.getPointerPosition();
  if (!pos) return null;
  
  let targetSeat: Konva.Node | null = null;
  let minDistance = Infinity;
  
  stage.find('.seat').forEach(seat => {
    const seatPos = seat.getAbsolutePosition();
    const dx = pos.x - seatPos.x;
    const dy = pos.y - seatPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Check if pointer is within the seat's scaled radius
    const seatCircle = seat as Konva.Circle;
    if (distance < seatCircle.radius() * stage.scaleX() && distance < minDistance) {
      minDistance = distance;
      targetSeat = seat;
    }
  });
  return targetSeat;
}

// Dynamic staging area functions

function drawCustomFields(guest: Guest, tableGroup: Konva.Group, seatX: number, seatY: number, angle: number): void {
  const customFieldConfigs = $customFields.get();
  
  // Filter to only visible custom fields that have values
  const visibleFields = customFieldConfigs.filter(config => 
    config.visibleInChart && guest[config.key] && guest[config.key]!.trim()
  );
  
  if (visibleFields.length === 0) {
    return;
  }
  
  // Create custom field values array (like nameParts for guest names)
  const fieldValues = visibleFields.map(config => guest[config.key]!.trim()).filter(Boolean);
  
  // Position custom fields on the opposite side of the seat from the guest name
  const oppositeAngle = angle + Math.PI;
  const { rotation, isFlipped } = calculateTextRotation(oppositeAngle);
  
  // Calculate text distance - account for multiple lines when flipped
  let textDistance = SEAT_RADIUS + SEAT_TEXT_OFFSET;
  if (isFlipped && fieldValues.length > 1) {
    textDistance += (fieldValues.length - 1) * LINE_HEIGHT/2;
  }
  
  // Position text at the tangent of the seat circle (opposite side)
  const customFieldX = seatX + textDistance * Math.cos(oppositeAngle);
  const customFieldY = seatY + textDistance * Math.sin(oppositeAngle);
  
  // Create a group for the rotated custom field text
  const customFieldGroup = new Konva.Group({
    x: customFieldX,
    y: customFieldY,
    rotation,
    name: 'customFields',
    visible: true,
    draggable: false,
    listening: true // Enable interaction for hover effects
  });
  
  // Store original values for hover tooltip
  const originalValues: string[] = [];
  
  // Create text for each visible custom field (same pattern as guest names)
  fieldValues.forEach((value, index) => {
    const originalValue = value; // Store the full text
    originalValues.push(originalValue);
    value = value.slice(0, 10); // Truncate for display
    
    const fieldText = createText({
      text: value,
      fontSize: FONT_SIZE * 0.8, // Smaller font (80% of guest name size)
      fill: '#2c5530',
      fontStyle: '400', // Thinner font weight (normal instead of bold)
      align: 'center',
      opacity: 0.75 // Slightly more subtle
    });
    
    // Position multiple fields in a vertical stack (EXACT same logic as guest names)
    let yOffset = -((fieldValues.length - 1 - index) * LINE_HEIGHT);
    
    // Center text horizontally
    fieldText.x(-fieldText.width() / 2);
    fieldText.y(yOffset);
    
    customFieldGroup.add(fieldText);
  });
  
  // Add background rectangle for hover detection
  const textBounds = customFieldGroup.getClientRect({ relativeTo: customFieldGroup });
  const padding = 4;
  const customFieldBg = new Konva.Rect({
    x: textBounds.x - padding,
    y: textBounds.y - padding,
    width: textBounds.width + padding * 2,
    height: textBounds.height + padding * 2,
    fill: 'transparent',
    cornerRadius: 3,
    name: 'customFieldBg'
  });
  customFieldGroup.add(customFieldBg);
  customFieldBg.moveToBottom();
  
  // Create tooltip for full text display using Konva.Label (the proper way!)
  let tooltip: Konva.Label | null = null;
  
  // Mouse move event - show tooltip that follows cursor (the Konva way!)
  customFieldGroup.on('mousemove', () => {
    // Filter to only values that are truncated (longer than 10 chars)
    const truncatedValues = originalValues.filter(value => value.length > 10);
    
    // Only show tooltip if there's truncated content
    if (truncatedValues.length > 0 && !tooltip) {
      // Create Konva Label (the proper tooltip component)
      tooltip = new Konva.Label({
        opacity: 0.9,
        visible: true,
        listening: false
      });
      
      // Create Tag (background with optional pointer)
      const tag = new Konva.Tag({
        fill: '#2c3e50',
        cornerRadius: 4,
        pointerDirection: 'down',
        pointerWidth: 8,
        pointerHeight: 6,
        shadowColor: 'black',
        shadowBlur: 4,
        shadowOffsetX: 2,
        shadowOffsetY: 2,
        shadowOpacity: 0.3
      });
      
      // Create Text content (join all truncated values)
      const text = createText({
        text: truncatedValues.join('\n'),
        fontSize: FONT_SIZE * 0.9,
        padding: 8,
        fill: 'white',
        lineHeight: 1.2
      });
      
      // Add components to label
      tooltip.add(tag);
      tooltip.add(text);
      
      // Add tooltip to layer
      layer.add(tooltip);
    }
    
    // Always update tooltip position to follow mouse cursor
    if (tooltip) {
      const mousePos = stage.getPointerPosition();
      if (mousePos) {
        // Convert from viewport coordinates to stage coordinates
        const stagePos = stage.position();
        const stageScale = stage.scaleX();
        
        const stageX = (mousePos.x - stagePos.x) / stageScale;
        const stageY = (mousePos.y - stagePos.y) / stageScale;
        
        tooltip.position({
          x: stageX + 10,
          y: stageY - 10
        });
      }
    }
    
    layer.batchDraw();
  });
  
  // Mouse leave event - hide tooltip
  customFieldGroup.on('mouseleave', () => {
    stage.container().style.cursor = 'default';
    customFieldBg.fill('transparent');
    
    // Remove tooltip if it exists
    if (tooltip) {
      tooltip.destroy();
      tooltip = null;
    }
    
    layer.batchDraw();
  });
  
  tableGroup.add(customFieldGroup);
}

/**
 * Calculate bounds of only the visible drawn elements using Konva's native getClientRect
 */
function calculateVisibleElementsBounds(): ContentBounds {
  if (!layer) {
    return { left: 0, top: 0, right: 100, bottom: 100, width: 100, height: 100 };
  }

  // Use Konva's built-in method to get the bounding box of all children
  const bounds = layer.getClientRect();
  
  // If no elements or invalid bounds, fall back to a default area
  if (!bounds || bounds.width <= 0 || bounds.height <= 0 || 
      isNaN(bounds.x) || isNaN(bounds.y) || isNaN(bounds.width) || isNaN(bounds.height)) {
    return { left: 0, top: 0, right: 400, bottom: 300, width: 400, height: 300 };
  }

  // Add some padding around the actual content
  const padding = 50;
  
  return {
    left: bounds.x - padding,
    top: bounds.y - padding,
    right: bounds.x + bounds.width + padding,
    bottom: bounds.y + bounds.height + padding,
    width: bounds.width + (padding * 2),
    height: bounds.height + (padding * 2)
  };
}

/**
 * Export the current stage as a high-resolution PDF (only visible drawn elements)
 */
export function exportToPDF(): void {
  if (!stage) {
    console.error('Stage not initialized');
    return;
  }

  // Get version name for filename and display
  const currentVersionId = $currentVersionId.get();
  const currentVersion = $availableVersions.get().find(v => v.id === currentVersionId);
  const versionName = currentVersion ? currentVersion.name.replace(/ /g, '_') : 'Untitled';
  const versionDisplayName = currentVersion ? currentVersion.name : 'Untitled';

  // Calculate bounds of only the visible drawn elements
  const bounds = calculateVisibleElementsBounds();
  
  // Convert to PDF page size (points, 72 DPI)
  const pdfWidth = bounds.width * 2; // Convert pixels to points
  const pdfHeight = bounds.height * 2;
  
  const pixelRatio = 4;
  const dataURL = stage.toDataURL({
    x: bounds.left,
    y: bounds.top,
    width: bounds.width,
    height: bounds.height,
    pixelRatio: pixelRatio,
    mimeType: 'image/png',
    quality: 1.0 // Maximum quality for PNG
  });
  
  // Create PDF document with custom size
  const doc = new jsPDF({
    orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
    unit: 'pt',
    format: [pdfWidth, pdfHeight],
    compress: false // Disable compression for better quality
  });
  
  // Add the canvas image to PDF with highest quality settings
  doc.addImage(
    dataURL,
    'PNG',
    0,
    0,
    pdfWidth,
    pdfHeight,
    undefined,
    'SLOW' // Use SLOW compression for better quality over speed
  );
  
  // Add version name in top-left corner
  doc.setFont('helvetica');
  doc.setFontSize(28);
  doc.setTextColor(0, 0, 0); // Black color
  doc.text(versionDisplayName, 50, 50);
  
  // Save the PDF with version name in filename
  const now = new Date();
  const dateString = now.toISOString().split('T')[0];
  const filename = `${versionName}-${dateString}.pdf`;
  doc.save(filename);
} 